// src/pages/AdminOrders.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const ORDER_STATUSES = [
  "Paid",
  "Processing",
  "Cancelled",
  "In Transit",
  "Failed Delivery",
  "Delivered"
];
const ITEMS_PER_PAGE = 10;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [alert, setAlert] = useState({ message: "", type: "", visible: false });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [updatedOrders, setUpdatedOrders] = useState({});
  const [selectedMessage, setSelectedMessage] = useState(null);
  const navigate = useNavigate();

  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const showAlert = (message, type = "success") => {
    setAlert({ message, type, visible: true });
    setTimeout(() => setAlert({ message: "", type: "", visible: false }), 3000);
  };

  useEffect(() => {
    const fetchOrdersAndDrivers = async () => {
      try {
        const [ordersRes, driversRes] = await Promise.all([
          fetch(`${BASE_URL}/api/admin/orders`, { credentials: "include" }),
          fetch(`${BASE_URL}/api/admin/drivers`, { credentials: "include" })
        ]);

        if (!ordersRes.ok || !driversRes.ok) {
          throw new Error("Failed to fetch orders or drivers");
        }

        const ordersData = await ordersRes.json();
        const driversData = await driversRes.json();

        setOrders(ordersData);
        setDrivers(driversData);
      } catch (error) {
        console.error("Error fetching admin orders or drivers:", error);
        showAlert("Failed to fetch orders or drivers", "error");
      }
    };

    fetchOrdersAndDrivers();
  }, [BASE_URL]);

  const updateOrder = async (orderId, changes) => {
    // only PATCH if there's something to change
    if (!changes || Object.keys(changes).length === 0) return;

    try {
      const res = await fetch(`${BASE_URL}/api/admin/orders/${orderId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(changes),
      });

      if (!res.ok) throw new Error("Failed to update order");

      const updatedOrder = await res.json();

      // swap in the updated order
      setOrders((prev) =>
        prev.map((o) => (o._id === updatedOrder._id ? updatedOrder : o))
      );

      // clear the pending changes for this order
      setUpdatedOrders((prev) => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
      showAlert("Order updated successfully", "success");

    } catch (err) {
      console.error("Error updating order:", err);
      showAlert("Failed to update order", "error");
    }
  };

  const handleFieldUpdate = (orderId, field, value) => {
  setUpdatedOrders((prev) => {
    const updated = { ...prev };
    if (!updated[orderId]) updated[orderId] = {};

    updated[orderId][field] = value;

    // ✅ If admin assigns a driver manually and order is still Paid, auto-change status to Processing
    if (field === "driverId") {
      const currentOrder = orders.find((o) => o._id === orderId);
      if (
        currentOrder &&
        (currentOrder.status === "Paid" || currentOrder.status === "paid")
      ) {
        updated[orderId].status = "Processing";
      }
    }

    return updated;
  });
};

  const handleSave = (orderId) => {
    const changes = updatedOrders[orderId];
    if (!changes) return;
    updateOrder(orderId, changes);
  };

  const filteredOrders = orders.filter((order) => {
    const matchStatus =
      statusFilter === "All" ||
      order.status?.toLowerCase() === statusFilter.toLowerCase();

    const orderId = order.orderNumber?.toLowerCase() || "";
    const customerName = order.user?.email?.toLowerCase() || "";

    const matchSearch =
      orderId.includes(search.toLowerCase()) ||
      customerName.includes(search.toLowerCase());

    return matchStatus && matchSearch;
  });

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white rounded shadow">
      {/* ✅ Banner Notification */}
      {alert.visible && (
        <div
          className={`mb-4 px-4 py-3 rounded text-sm ${
            alert.type === "success"
              ? "bg-green-100 text-green-800"
              : alert.type === "error"
              ? "bg-red-100 text-red-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {alert.message}
        </div>
      )}
      <h1 className="text-2xl font-bold mb-2">All Orders</h1>
      <p className="mb-6 text-gray-600">Manage and assign customer orders.</p>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
        <input
          type="text"
          placeholder="Search by Order ID or Customer"
          className="border p-2 rounded w-full md:w-1/3"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
        <select
          className="border p-2 rounded w-full md:w-1/4"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All">All</option>
          {ORDER_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {/* Orders Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border text-sm">
          <thead>
            <tr className="bg-gray-200 text-left">
              <th className="p-3 border">Order ID</th>
              <th className="p-3 border">Customer</th>
              <th className="p-3 border">Status</th>
              <th className="p-3 border">Gift Message</th>
              <th className="p-3 border">Assign Driver</th>
              <th className="p-3 border">Change Status</th>
              <th className="p-3 border">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="7" className="p-4 text-center text-gray-500">
                  No orders found.
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order) => (
                <tr key={order._id} className="even:bg-gray-50">
                  <td className="p-3 border">
                    <button
                      onClick={() => navigate(`/admin/orders/${order._id}`)}
                      className="text-blue-600 hover:underline font-medium"
                    >
                      {order.orderNumber || "N/A"}
                    </button>
                  </td>
                  <td className="p-3 border">{order.user?.email || "N/A"}</td>
                  <td className="p-3 border">{order.status || "N/A"}</td>

                  {/* Gift Message */}
                  <td className="p-3 border text-center">
                    {order.giftMessage ? (
                      <button
                        onClick={() => setSelectedMessage(order.giftMessage)}
                        className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs hover:bg-purple-200"
                      >
                        View Message
                      </button>
                    ) : (
                      <span className="italic text-gray-400">N/A</span>
                    )}
                  </td>

                  {/* Driver dropdown */}
                  <td className="p-3 border">
                    <select
                      className="border p-1 text-sm rounded w-full"
                      value={
                        updatedOrders[order._id]?.driverId ??
                        (typeof order.driver === "object"
                          ? order.driver?._id
                          : order.driver) ??
                        ""
                      }
                      onChange={(e) =>
                        handleFieldUpdate(order._id, "driverId", e.target.value)
                      }
                    >
                      <option value="">Unassigned</option>
                      {drivers
                        .filter((d) => d.status === "active") // ✅ only include active drivers
                        .map((d) => (
                          <option key={d._id} value={d._id}>
                            {(d.fullName || d.name || "Driver")} ({d.email})
                          </option>
                        ))}
                    </select>
                  </td>

                  {/* Status dropdown */}
                  <td className="p-3 border">
                    <select
                      className="border p-1 text-sm rounded w-full"
                      value={updatedOrders[order._id]?.status || order.status || ""}
                      onChange={(e) =>
                        handleFieldUpdate(order._id, "status", e.target.value)
                      }
                    >
                      {ORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Save button */}
                  <td className="p-3 border text-center">
                    {updatedOrders[order._id] &&
                      Object.keys(updatedOrders[order._id]).length > 0 && (
                        <button
                          className="bg-blue-600 text-white px-3 py-1 rounded text-xs"
                          onClick={() => handleSave(order._id)}
                        >
                          Save
                        </button>
                      )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Modal for Gift Message */}
        {selectedMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
              <h2 className="text-lg font-semibold mb-4">Gift Message</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage}</p>
              <div className="mt-6 text-right">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1 border rounded"
          >
            Prev
          </button>
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={`px-3 py-1 border rounded ${
                currentPage === i + 1 ? "bg-blue-600 text-white" : ""
              }`}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1 border rounded"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
